import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Trash2, Loader2, AlertTriangle, ExternalLink } from "lucide-react";
import { useI18n } from "../../i18n";

const API = "/api";

/** Store-compliant account deletion from inside the authenticated account. */
export const AccountDeletionSection = () => {
  const { t } = useI18n();
  const [status, setStatus] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [reason, setReason] = useState("");
  const [working, setWorking] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/account/deletion-status`);
      setStatus(r.data);
    } catch (_e) {
      setStatus({ status: "none" });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const request = async (e) => {
    e.preventDefault();
    setWorking(true);
    try {
      await axios.post(`${API}/account/deletion-request`, {
        password, confirm: true, reason, client: "web",
      });
      toast.success(t("app.set.del_requested"));
      setPassword("");
      setConfirm(false);
      setOpen(false);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail === "invalid_password"
        ? t("app.set.del_bad_password")
        : t("app.set.del_request_error"));
    } finally {
      setWorking(false);
    }
  };

  const cancel = async () => {
    setWorking(true);
    try {
      await axios.post(`${API}/account/deletion-cancel`, {});
      toast.success(t("app.set.del_cancelled"));
      load();
    } catch (_e) {
      toast.error(t("app.set.del_cancel_error"));
    } finally {
      setWorking(false);
    }
  };

  if (!status) return null;
  const scheduled = status.status === "scheduled" || status.status === "pending";

  return (
    <section aria-labelledby="deletion-heading"
             className="bg-red-500/5 border border-red-500/20 rounded-2xl p-5 md:p-6"
             data-testid="account-deletion-section">
      <h2 id="deletion-heading" className="text-base md:text-lg font-semibold text-[#0B2545] flex items-center gap-2 mb-3">
        <Trash2 className="w-5 h-5 text-red-400" aria-hidden="true" /> {t("app.set.del_title")}
      </h2>

      {scheduled ? (
        <div role="status" data-testid="deletion-scheduled">
          <p className="text-sm text-red-700 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
            {t("app.set.del_scheduled")}{" "}
            <strong>{(status.scheduled_for || "").slice(0, 10)}</strong>.{" "}
            {t("app.set.del_scheduled_note")}
          </p>
          {status.can_cancel && (
            <Button onClick={cancel} disabled={working}
                    className="mt-4 bg-[#1B84FF] hover:bg-[#0F6FDB] text-white font-semibold"
                    data-testid="deletion-cancel-btn">
              {working ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : t("app.set.del_cancel_btn")}
            </Button>
          )}
        </div>
      ) : (
        <>
          <p className="text-sm text-slate-600">
            {t("app.set.del_body", { n: status.grace_days || 14 })}
          </p>
          {!open ? (
            <Button variant="ghost" onClick={() => setOpen(true)}
                    className="mt-4 text-red-700 hover:text-red-700 hover:bg-red-500/10"
                    data-testid="deletion-open-btn">{t("app.set.del_open_btn")}</Button>
          ) : (
            <form onSubmit={request} className="mt-5 space-y-4" data-testid="deletion-form">
              <div>
                <Label htmlFor="deletion-password" className="text-sm text-slate-700">
                  {t("app.set.del_password")}
                </Label>
                <Input id="deletion-password" type="password" required autoComplete="current-password"
                       value={password} onChange={(e) => setPassword(e.target.value)}
                       className="mt-1 bg-slate-100 border-slate-300 text-[#0B2545]"
                       data-testid="deletion-password-input" />
              </div>
              <div>
                <Label htmlFor="deletion-reason" className="text-sm text-slate-700">
                  {t("app.set.del_reason")}
                </Label>
                <Input id="deletion-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                       className="mt-1 bg-slate-100 border-slate-300 text-[#0B2545]"
                       data-testid="deletion-reason-input" />
              </div>
              <label className="flex items-start gap-3 text-sm text-slate-700">
                <input type="checkbox" checked={confirm} required
                       onChange={(e) => setConfirm(e.target.checked)}
                       className="mt-1 w-4 h-4 accent-red-500"
                       data-testid="deletion-confirm-checkbox" />
                {t("app.set.del_understand")}
              </label>
              <div className="flex flex-wrap gap-3">
                <Button type="submit" disabled={working || !confirm}
                        className="bg-red-500 hover:bg-red-600 text-white font-semibold"
                        data-testid="deletion-submit-btn">
                  {working ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : t("app.set.del_submit")}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}
                        className="text-slate-600" data-testid="deletion-abort-btn">{t("app.set.del_abort")}</Button>
              </div>
            </form>
          )}
        </>
      )}

      <Link to="/eliminar-cuenta"
            className="inline-flex items-center gap-2 mt-5 text-xs text-slate-500 hover:text-slate-700"
            data-testid="deletion-public-resource-link">
        {t("app.set.del_policy_link")}
        <ExternalLink className="w-3 h-3" aria-hidden="true" />
      </Link>
    </section>
  );
};

export default AccountDeletionSection;
